// ============================================
// Store Selection Store - Zustand
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Store } from '@conai/shared-types';

interface StoreSelectionState {
  // Selected store for multi-store operations
  selectedStore: Store | null;
  selectedStoreId: string | null;

  // Available stores list (cached from API)
  availableStores: Store[];
  isLoadingStores: boolean;

  // Actions
  setSelectedStore: (store: Store | null) => void;
  setSelectedStoreById: (storeId: string | null) => void;
  setAvailableStores: (stores: Store[]) => void;
  setLoadingStores: (loading: boolean) => void;
  clearSelection: () => void;
  reset: () => void;
}

const initialState = {
  selectedStore: null,
  selectedStoreId: null,
  availableStores: [],
  isLoadingStores: false,
};

export const useStoreSelectionStore = create<StoreSelectionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSelectedStore: (store) =>
        set({
          selectedStore: store,
          selectedStoreId: store?.id ?? null,
        }),

      setSelectedStoreById: (storeId) => {
        const stores = get().availableStores;
        const store = stores.find((s) => s.id === storeId) ?? null;
        set({
          selectedStore: store,
          selectedStoreId: storeId,
        });
      },

      setAvailableStores: (stores) => {
        const currentSelectedId = get().selectedStoreId;
        const currentStoreStillValid = stores.some((s) => s.id === currentSelectedId);

        set({
          availableStores: stores,
          // Auto-select first store if no selection or current selection is invalid
          selectedStore: currentStoreStillValid
            ? stores.find((s) => s.id === currentSelectedId) ?? null
            : stores[0] ?? null,
          selectedStoreId: currentStoreStillValid
            ? currentSelectedId
            : stores[0]?.id ?? null,
        });
      },

      setLoadingStores: (isLoadingStores) => set({ isLoadingStores }),

      clearSelection: () =>
        set({
          selectedStore: null,
          selectedStoreId: null,
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'conai-admin-store-selection',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedStoreId: state.selectedStoreId,
      }),
    }
  )
);

// ============================================
// Selectors
// ============================================

export const selectSelectedStore = (state: StoreSelectionState) => state.selectedStore;
export const selectSelectedStoreId = (state: StoreSelectionState) => state.selectedStoreId;
export const selectAvailableStores = (state: StoreSelectionState) => state.availableStores;
export const selectIsLoadingStores = (state: StoreSelectionState) => state.isLoadingStores;
export const selectHasStoreSelected = (state: StoreSelectionState) => !!state.selectedStore;

// ============================================
// Hooks
// ============================================

/**
 * Get the current selected store - throws if none selected
 */
export function useRequiredStore(): Store {
  const store = useStoreSelectionStore(selectSelectedStore);
  if (!store) {
    throw new Error('No store selected. Please select a store first.');
  }
  return store;
}

/**
 * Check if a specific store is selected
 */
export function useIsStoreSelected(storeId: string): boolean {
  const selectedId = useStoreSelectionStore(selectSelectedStoreId);
  return selectedId === storeId;
}
